import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../utils/api";

const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: "", retailer: "" });
  const [showShortUrls, setShowShortUrls] = useState(false);
  const [colWidths, setColWidths] = useState({});
  const resizingRef = useRef(null);

  const onMouseDown = useCallback((col, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const th = e.target.parentElement;
    const startWidth = th.offsetWidth;

    const onMouseMove = (moveE) => {
      const newWidth = Math.max(60, startWidth + (moveE.clientX - startX));
      setColWidths((prev) => ({ ...prev, [col]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      resizingRef.current = null;
    };

    resizingRef.current = col;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const ResizeTh = ({ col, children, align }) => (
    <th style={{ width: colWidths[col] || undefined, minWidth: 60, textAlign: align, position: "relative", overflow: "hidden" }}>
      {children}
      <div className="resize-handle" onMouseDown={(e) => onMouseDown(col, e)} />
    </th>
  );

  useEffect(() => {
    api.get("/api/options").then((res) => setOptions(res.data)).catch(console.error);
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set("search", filters.search);
      if (filters.retailer) params.set("retailer", filters.retailer);
      const res = await api.get(`/api/analytics/product-codes-report?${params}`);
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadReport(); }, []);

  const applyFilters = () => { loadReport(); };

  const exportToExcel = () => {
    if (!data) return;
    const rows = data.map((r) => ({
      "UPC": r.upc,
      "Cust Item #": r.customerItemNumber,
      "Item ID": r.itemId,
      "Description": r.productName,
      "Retailer": r.retailer,
      "Front ID": r.frontId,
      "Front Format": r.frontFormat,
      "Front URL": showShortUrls ? r.frontShortUrl : r.frontDestUrl,
      "Back ID": r.backId,
      "Back Format": r.backFormat,
      "Back URL": showShortUrls ? r.backShortUrl : r.backDestUrl,
      "Other Codes": r.otherCodes,
      "Total Clicks": r.totalClicks,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map((k) => ({ wch: Math.max(k.length, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product_Codes");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Product_Codes_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const hasFilters = filters.search || filters.retailer;

  return (
    <div>
      <div className="tl-card">
        <div className="tl-page-header">
          <h2>Product Codes Report</h2>
          {data && (
            <button className="btn btn-tl-primary" onClick={exportToExcel}>
              Export to Excel
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="tl-card tl-card-compact mb-3" style={{ flexShrink: 0 }}>
        <div className="d-flex gap-2 align-items-end">
          <div style={{ flex: 3 }}>
            <label className="tl-form-label">Search (Name, UPC, Item ID)</label>
            <input className="form-control form-control-sm tl-form-control" placeholder="Filter..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} onKeyDown={(e) => e.key === "Enter" && applyFilters()} />
          </div>
          <div style={{ flex: 2 }}>
            <label className="tl-form-label">Retailer</label>
            <select className="form-select form-select-sm tl-form-control" value={filters.retailer} onChange={(e) => setFilters({ ...filters, retailer: e.target.value })}>
              <option value="">All</option>
              {options.retailers?.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="tl-form-label">URLs</label>
            <div className="d-flex gap-1">
              <button className={`btn btn-sm ${!showShortUrls ? "btn-tl-primary" : "btn-outline-secondary"}`} style={{ borderRadius: "8px", fontSize: "11px", padding: "2px 8px" }} onClick={() => setShowShortUrls(false)}>Destination</button>
              <button className={`btn btn-sm ${showShortUrls ? "btn-tl-primary" : "btn-outline-secondary"}`} style={{ borderRadius: "8px", fontSize: "11px", padding: "2px 8px" }} onClick={() => setShowShortUrls(true)}>Short URL</button>
            </div>
          </div>
          <button className="btn btn-sm btn-tl-primary" onClick={applyFilters}>Apply</button>
          {hasFilters && (
            <button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "8px" }} onClick={() => { setFilters({ search: "", retailer: "" }); setTimeout(loadReport, 0); }}>Clear</button>
          )}
          <span className="text-muted small text-nowrap">{data ? `${data.length} products` : ""}</span>
        </div>
      </div>

      {/* Report table */}
      {loading && <div className="text-center py-5"><div className="spinner-border text-white" /></div>}
      {data && !loading && (
        <div className="tl-card tl-card-compact" style={{ padding: 0 }}>
          <div style={{ overflow: "auto", maxHeight: "calc(100vh - 280px)" }}>
            <table className="tl-table resizable-table" style={{ fontSize: "13px", whiteSpace: "nowrap" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <ResizeTh col="upc">UPC</ResizeTh>
                  <ResizeTh col="custItem">Cust Item #</ResizeTh>
                  <ResizeTh col="itemId">Item ID</ResizeTh>
                  <ResizeTh col="desc">Description</ResizeTh>
                  <ResizeTh col="retailer">Retailer</ResizeTh>
                  <ResizeTh col="frontId">Front ID</ResizeTh>
                  <ResizeTh col="frontFmt">Front Format</ResizeTh>
                  <ResizeTh col="frontUrl">Front URL</ResizeTh>
                  <ResizeTh col="backId">Back ID</ResizeTh>
                  <ResizeTh col="backFmt">Back Format</ResizeTh>
                  <ResizeTh col="backUrl">Back URL</ResizeTh>
                  <ResizeTh col="other">Other Codes</ResizeTh>
                  <ResizeTh col="clicks" align="right">Clicks</ResizeTh>
                </tr>
              </thead>
              <tbody>
                {data.map((r, i) => (
                  <tr key={i} style={{ cursor: "default" }}>
                    <td style={{ fontFamily: "monospace" }}>{r.upc || "—"}</td>
                    <td>{r.customerItemNumber || "—"}</td>
                    <td>{r.itemId || "—"}</td>
                    <td>{r.productName}</td>
                    <td>{r.retailer || "—"}</td>
                    <td style={{ color: r.frontId === "NO CODE" ? "#ef4444" : "#22c55e", fontFamily: "monospace", fontWeight: 600 }}>
                      {r.frontId}
                    </td>
                    <td className="text-muted" style={{ fontSize: "12px" }}>{r.frontFormat}</td>
                    <td className="text-muted" style={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {showShortUrls ? r.frontShortUrl : r.frontDestUrl || "—"}
                    </td>
                    <td style={{ color: r.backId === "NO CODE" ? "#ef4444" : "#22c55e", fontFamily: "monospace", fontWeight: 600 }}>
                      {r.backId}
                    </td>
                    <td className="text-muted" style={{ fontSize: "12px" }}>{r.backFormat}</td>
                    <td className="text-muted" style={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {showShortUrls ? r.backShortUrl : r.backDestUrl || "—"}
                    </td>
                    <td className="text-muted" style={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.otherCodes || "—"}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{r.totalClicks.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
